import { config } from "../config.ts";
import { codeChallenge, randomString } from "./pkce.ts";

/**
 * Connexion au compte Shopware, par le même parcours que l'app mobile (plugin CustomsTabsAuth) :
 * redirection vers /app/auth/login avec PKCE, retour sur /auth/callback, échange du code
 * contre des jetons sur /store-api/auth/token, renouvellement sur /store-api/auth/refresh.
 */
export interface Tokens {
  accessToken: string;
  refreshToken: string;
  /** Horodatage (ms) d'expiration du jeton d'accès. */
  expiresAt: number;
}

export interface Customer {
  firstName?: string;
  email?: string;
}

const TOKENS_KEY = "msb-reader:auth";
const PENDING_KEY = "msb-reader:pending-login";

/** Cause d'un échec de connexion, traduite par la page de retour. */
export type AuthErrorReason = "cancelled" | "invalid-link" | "failed" | "expired";

export class AuthError extends Error {
  constructor(
    readonly reason: AuthErrorReason,
    message: string,
    /** Explication renvoyée par la boutique (error_description), affichée telle quelle. */
    readonly detail?: string,
  ) {
    super(message);
    this.name = "AuthError";
  }
}

function read<T>(storage: Storage, key: string): T | undefined {
  try {
    const raw = storage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : undefined;
  } catch {
    return undefined;
  }
}

function write(storage: Storage, key: string, value: unknown | undefined): void {
  try {
    if (value === undefined) storage.removeItem(key);
    else storage.setItem(key, JSON.stringify(value));
  } catch {
    // Stockage indisponible : la session ne survivra pas au rechargement.
  }
}

export const redirectUri = () => `${window.location.origin}/auth/callback`;

export function loadTokens(): Tokens | undefined {
  return read<Tokens>(localStorage, TOKENS_KEY);
}

export function logout(): void {
  write(localStorage, TOKENS_KEY, undefined);
}

/** Lit le prénom dans le jeton d'accès (affichage uniquement, pas de vérification). */
export function customerFromToken(token: string | undefined): Customer | undefined {
  try {
    const binary = atob(token!.split(".")[1]!.replace(/-/g, "+").replace(/_/g, "/"));
    const payload = JSON.parse(new TextDecoder().decode(Uint8Array.from(binary, (c) => c.charCodeAt(0))));
    return { firstName: payload.customer?.firstName, email: payload.customer?.email };
  } catch {
    return undefined;
  }
}

export async function startLogin(returnTo = "/"): Promise<void> {
  const verifier = randomString(48);
  const state = randomString(16);
  write(sessionStorage, PENDING_KEY, { verifier, state, returnTo });
  const params = new URLSearchParams({
    redirect_uri: redirectUri(),
    state,
    code_challenge: await codeChallenge(verifier),
    code_challenge_method: "S256",
  });
  window.location.assign(`${config.shopUrl}/app/auth/login?${params}`);
}

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in?: number;
}

function toTokens(body: TokenResponse): Tokens {
  if (!body.access_token || !body.refresh_token) throw new AuthError("failed", "Réponse de connexion incomplète.");
  return {
    accessToken: body.access_token,
    refreshToken: body.refresh_token,
    expiresAt: Date.now() + (body.expires_in ?? 86400) * 1000,
  };
}

async function postShop(path: string, body: unknown, extraHeaders: Record<string, string> = {}): Promise<Response> {
  return fetch(`${config.shopUrl}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "sw-access-key": config.shopAccessKey, ...extraHeaders },
    body: JSON.stringify(body),
  });
}

/** Termine la connexion au retour de Shopware ; renvoie la page à rouvrir. */
export async function completeLogin(search: string): Promise<string> {
  const query = new URLSearchParams(search);
  const pending = read<{ verifier: string; state: string; returnTo: string }>(sessionStorage, PENDING_KEY);
  write(sessionStorage, PENDING_KEY, undefined);
  if (query.get("error")) {
    const detail = query.get("error_description") ?? undefined;
    throw new AuthError("cancelled", detail ?? "Connexion annulée.", detail);
  }
  const code = query.get("code");
  if (!pending || !code || query.get("state") !== pending.state) {
    throw new AuthError("invalid-link", "Lien de connexion invalide ou expiré. Recommencez.");
  }
  const response = await postShop("/store-api/auth/token", {
    code,
    code_verifier: pending.verifier,
    redirect_uri: redirectUri(),
  });
  if (!response.ok) throw new AuthError("failed", "La connexion a échoué. Recommencez.");
  write(localStorage, TOKENS_KEY, toTokens(await response.json()));
  return pending.returnTo.startsWith("/") ? pending.returnTo : "/";
}

let refreshing: Promise<Tokens | undefined> | undefined;

async function refresh(tokens: Tokens): Promise<Tokens | undefined> {
  refreshing ??= (async () => {
    try {
      const response = await postShop("/store-api/auth/refresh", { refresh_token: tokens.refreshToken }, { "sw-context-token": "" });
      if (!response.ok) return undefined;
      const next = toTokens(await response.json());
      write(localStorage, TOKENS_KEY, next);
      return next;
    } catch {
      return undefined;
    } finally {
      refreshing = undefined;
    }
  })();
  return refreshing;
}

/** Jeton d'accès valide, renouvelé si besoin ; `undefined` s'il faut se reconnecter. */
export async function accessToken(forceRefresh = false): Promise<string | undefined> {
  const tokens = loadTokens();
  if (!tokens) return undefined;
  if (!forceRefresh && tokens.expiresAt - 60_000 > Date.now()) return tokens.accessToken;
  const next = await refresh(tokens);
  if (!next) logout();
  return next?.accessToken;
}

/** Appel authentifié, avec un renouvellement et une nouvelle tentative sur 401. */
export async function authorizedFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const attempt = async (token: string | undefined) => {
    if (!token) throw new AuthError("expired", "Session expirée, reconnectez-vous.");
    return fetch(url, { ...init, headers: { ...(init.headers as Record<string, string>), Authorization: `Bearer ${token}` } });
  };
  const response = await attempt(await accessToken());
  if (response.status !== 401) return response;
  return attempt(await accessToken(true));
}
